import {
  convertPosProviderToBackend,
  convertPosProviderToFrontend,
} from "./pos-converter";
import {
  convertHoursToBackend,
  convertHoursToFrontend,
  convertIAgentHoursToSchedule,
} from "./hours-converter";
import { IBackendAgentBlueprint } from "../../types/backend";
import { AgentType, IAgentBlueprint } from "../../types/base";
import { defaultTwilioOverride, emptyHours } from "../../defaults/basic";
import {
  convertPrepTimeToFrontend,
  convertPrepTimeToBackend,
} from "./menu-converter";
import {
  isHoursEmpty,
  convertHolidayHoursToFrontend,
  convertHolidayHoursToBackend,
  FAQ_LOCAL_ID_PREFIX,
} from "@loman/utils";

export function convertAgentBlueprintToFrontend(
  agentBlueprint: IBackendAgentBlueprint,
): IAgentBlueprint {
  const posProvider = convertPosProviderToFrontend(
    agentBlueprint.posProvider || null,
  );
  const reservationProvider = agentBlueprint.reservationProvider;
  const twilioOverride = agentBlueprint.twilioOverride;
  const messages = Object.entries(agentBlueprint.messages || {}).map(
    ([key, value]) => ({
      key,
      value: String(value),
    }),
  );
  const transfers = Object.entries(agentBlueprint.transfers || {}).map(
    ([key, value]) => ({
      key,
      value: Array.isArray(value) ? value.map(String) : String(value),
    }),
  );
  const legacyPrepTime = agentBlueprint.posProvider?.orderingModuleConfig
    ?.prepTime
    ? String(
        convertPrepTimeToFrontend(
          agentBlueprint.posProvider?.orderingModuleConfig?.prepTime,
        ),
      )
    : null;

  return {
    accountId: agentBlueprint.accountId,
    address: agentBlueprint.address,
    addressComponents: agentBlueprint.addressComponents,
    allowAnon: agentBlueprint.allowAnon,
    createdAt: agentBlueprint.createdAt,
    deletedAt: agentBlueprint.deletedAt || null,
    enabled: agentBlueprint.enabled ?? true,
    fallbackPhoneNumber: agentBlueprint.fallbackPhoneNumber || null,
    faqs: agentBlueprint.faqs || [],
    greetingMessageOpen: agentBlueprint.greetingMessageOpen?.trim() || "",
    greetingMessageClosed: agentBlueprint.greetingMessageClosed?.trim() || "",
    greetingMessageOrderingClosed:
      agentBlueprint.greetingMessageOrderingClosed?.trim() || "",
    hours: convertHoursToFrontend(agentBlueprint.hours) ?? emptyHours,
    hoursOrdering: convertHoursToFrontend(agentBlueprint.hoursOrdering ?? null),
    hoursLoman: convertHoursToFrontend(agentBlueprint.hoursLoman || null),
    hubspotDealId: agentBlueprint.hubspotDealId || undefined,
    id: agentBlueprint.id?.trim() || undefined,
    messages: messages.length > 0 ? messages : [{ key: "", value: "" }],
    name: agentBlueprint.name.trim(),
    panic: agentBlueprint.panic,
    pausedUntil: agentBlueprint.pausedUntil || null,
    phoneNumber: agentBlueprint.phoneNumber || null,
    placeId: agentBlueprint.placeId,
    posExternalId: agentBlueprint.posExternalId,
    reservationExternalId: agentBlueprint.reservationExternalId,
    pickupPrepTime: agentBlueprint.pickupPrepTime
      ? String(convertPrepTimeToFrontend(agentBlueprint.pickupPrepTime))
      : legacyPrepTime,
    deliveryPrepTime: agentBlueprint.deliveryPrepTime
      ? String(convertPrepTimeToFrontend(agentBlueprint.deliveryPrepTime))
      : legacyPrepTime,
    customPrepTimeEnabled: agentBlueprint.customPrepTimeEnabled,
    customPrepTimeHours: agentBlueprint.customPrepTimeHours || emptyHours,
    holidayHours: convertHolidayHoursToFrontend(agentBlueprint.holidayHours),
    pos: posProvider ? posProvider.provider : null,
    posProvider: posProvider ? posProvider : null,
    prompt: agentBlueprint.prompt?.trim() || null,
    reservationProvider: reservationProvider || null,
    slug: agentBlueprint.slug?.trim() || undefined,
    smsPhoneNumber: agentBlueprint.smsPhoneNumber || null,
    startedAt: agentBlueprint.startedAt,
    onboardingStartedAt: agentBlueprint.onboardingStartedAt,
    streamConnectedAt: agentBlueprint.streamConnectedAt,
    stripeAccountId: agentBlueprint.stripeAccountId,
    stripeChargesEnabled: agentBlueprint.stripeChargesEnabled,
    stripeCustomerId: agentBlueprint.stripeCustomerId || null,
    stripeSubscriptionId: agentBlueprint.stripeSubscriptionId || null,
    timezone: agentBlueprint.timezone,
    transfers: transfers.length > 0 ? transfers : [{ key: "", value: "" }],
    twilioOverride: twilioOverride
      ? {
          enabled: true,
          sid: twilioOverride.sid?.trim() || "",
          auth: twilioOverride.auth?.trim() || "",
        }
      : defaultTwilioOverride,
    twoPartyConsent: agentBlueprint.twoPartyConsent ?? true,
    type: agentBlueprint.type as AgentType,
    updatedAt: agentBlueprint.updatedAt,
    users: agentBlueprint.users || null,
    voicemailConfig: agentBlueprint.voicemailConfig || null,
  };
}

export function convertAgentBlueprintToBackend(
  agentBlueprint: IAgentBlueprint | null,
): IBackendAgentBlueprint | null {
  if (!agentBlueprint) {
    return null;
  }
  const transfers =
    agentBlueprint.transfers
      ?.filter((t) => t.key.trim() && t.value)
      .reduce<
        Record<string, string | string[]>
      >((acc, { key, value }) => ({ ...acc, [key.trim()]: value }), {}) || {};

  const messages =
    agentBlueprint.messages
      ?.filter((m) => m.key.trim() && m.value.trim())
      .reduce<
        Record<string, string>
      >((acc, { key, value }) => ({ ...acc, [key.trim()]: value.trim() }), {}) ||
    {};

  const faqs = agentBlueprint.faqs
    ?.filter((faq) => faq.question.trim() && faq.answer.trim())
    .map(({ id, ...tail }) => {
      if (id?.startsWith(FAQ_LOCAL_ID_PREFIX)) {
        return tail;
      }
      return { id, ...tail };
    });

  const posProvider = convertPosProviderToBackend(agentBlueprint.posProvider);

  return {
    accountId: agentBlueprint.accountId,
    allowAnon: agentBlueprint.allowAnon ?? undefined,
    fallbackPhoneNumber: agentBlueprint.fallbackPhoneNumber || null,
    faqs: faqs || [],
    greetingMessageOpen: agentBlueprint.greetingMessageOpen.trim(),
    greetingMessageClosed: agentBlueprint.greetingMessageClosed.trim(),
    greetingMessageOrderingClosed:
      agentBlueprint.greetingMessageOrderingClosed?.trim(),
    hours:
      convertHoursToBackend(agentBlueprint.hours || emptyHours) || emptyHours,
    hoursOrdering: convertHoursToBackend(agentBlueprint.hoursOrdering ?? null),
    hoursLoman: agentBlueprint.hoursLoman
      ? isHoursEmpty(convertIAgentHoursToSchedule(agentBlueprint.hoursLoman))
        ? null
        : convertHoursToBackend(agentBlueprint.hoursLoman)
      : undefined,
    hubspotDealId: agentBlueprint.hubspotDealId || undefined,
    id: agentBlueprint.id?.trim() || undefined,
    messages: messages,
    name: agentBlueprint.name.trim(),
    panic: agentBlueprint.panic ?? undefined,
    pausedUntil: agentBlueprint.pausedUntil || undefined,
    phoneNumber: agentBlueprint.phoneNumber || null,
    placeId: agentBlueprint.placeId,
    posExternalId: agentBlueprint.posExternalId?.trim() || undefined,
    reservationExternalId: agentBlueprint.reservationExternalId?.trim() || null,
    pickupPrepTime:
      agentBlueprint.pickupPrepTime !== undefined &&
      agentBlueprint.pickupPrepTime !== null
        ? convertPrepTimeToBackend(parseInt(agentBlueprint.pickupPrepTime))
        : null,
    deliveryPrepTime:
      agentBlueprint.deliveryPrepTime !== undefined &&
      agentBlueprint.deliveryPrepTime !== null
        ? convertPrepTimeToBackend(parseInt(agentBlueprint.deliveryPrepTime))
        : null,
    customPrepTimeEnabled: agentBlueprint.customPrepTimeEnabled ?? false,
    customPrepTimeHours: agentBlueprint.customPrepTimeHours || emptyHours,
    holidayHours: convertHolidayHoursToBackend(agentBlueprint.holidayHours),
    posProvider: posProvider ? posProvider : null,
    prompt: agentBlueprint.prompt?.trim() ? agentBlueprint.prompt.trim() : null,
    reservationProvider: agentBlueprint.reservationProvider || null,
    slug: agentBlueprint.slug?.trim() ? agentBlueprint.slug.trim() : undefined,
    smsPhoneNumber: agentBlueprint.smsPhoneNumber || undefined,
    streamConnectedAt: agentBlueprint.streamConnectedAt || undefined,
    timezone: agentBlueprint.timezone,
    transfers: transfers,
    twilioOverride: agentBlueprint.twilioOverride?.enabled
      ? {
          sid: agentBlueprint.twilioOverride.sid || "",
          auth: agentBlueprint.twilioOverride.auth || "",
        }
      : null,
    twoPartyConsent: agentBlueprint.twoPartyConsent,
    type: agentBlueprint.type,
    voicemailConfig: agentBlueprint.voicemailConfig || null,
  };
}
